import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import { useApolloClient } from "lib/apollo/client";
import { parsedDate } from "lib/dateUtils";
import { useUserBookRead } from "store/books/booksSelector";
import { removeUserBookRead, setUserBookRead } from "store/books/booksSlice";
import { audioReadingFormat } from "types/ReadingFormatType";
import { UserBookSerializersUserBookDate } from "types/serializers";

import { deleteUserBookRead } from "./actions/delete";
import { insertUserBookRead } from "./actions/insert";
import { updateUserBookRead } from "./actions/update";

export interface FromToDate {
  from?: Date;
  to?: Date;
}

const catchErrorMessage = (error: string) => {
  toast.error(`Something went wrong: ${error}`);
  return error;
};

interface Props {
  bookId: number;
  userBookReadId: number | null;
  settings?: {
    userBookRead?: UserBookSerializersUserBookDate;
  };
}
export default function useUserBookReadService({
  bookId,
  userBookReadId: initialUserBookReadId,
  settings = {},
}: Props) {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const [userBookReadId, setUserBookReadId] = useState<number | null>(
    initialUserBookReadId || null,
  );
  const storeUserBookRead = useUserBookRead(bookId, userBookReadId);
  const userBookRead = settings.userBookRead || storeUserBookRead;

  async function updateUserBookReadCache(
    newUserBookRead: UserBookSerializersUserBookDate | null,
  ) {
    if (newUserBookRead) {
      dispatch(setUserBookRead({ bookId, userBookRead: newUserBookRead }));
    }
    return newUserBookRead;
  }

  const updateEdition = (editionId: number) => {
    if (userBookRead?.id) {
      // Update
      return updateUserBookRead({
        client,
        id: userBookRead.id,
        object: {
          ...userBookRead,
          ...{ editionId },
        },
      })
        .then(updateUserBookReadCache)
        .catch(catchErrorMessage);
    } else {
      // Insert
      return insertUserBookRead({
        client,
        userBookId: userBookRead.userBookId,
        userBookRead: {
          ...userBookRead,
          ...{ editionId },
        } as unknown as UserBookSerializersUserBookDate,
      })
        .then(updateUserBookReadCache)
        .catch(catchErrorMessage);
    }
  };

  const updateDates = (date: FromToDate) => {
    const startedAt = date.from ? parsedDate(date.from) : null;
    const finishedAt = date.to ? parsedDate(date.to) : null;

    if (userBookRead?.id) {
      // Update
      return updateUserBookRead({
        client,
        id: userBookRead.id,
        object: {
          ...userBookRead,
          ...{
            startedAt,
            finishedAt,
          },
        },
      }).then(updateUserBookReadCache);
    } else {
      // Insert
      return insertUserBookRead({
        client,
        userBookId: userBookRead.userBookId,
        userBookRead: {
          startedAt,
          finishedAt,
        } as unknown as UserBookSerializersUserBookDate,
      }).then(updateUserBookReadCache);
    }
  };

  const updateProgress = newProgress => {
    if (!userBookRead?.id) {
      toast.error(
        "Something went wrong updating your progress. Please refresh the page.",
      );
      return Promise.resolve("No date read to update.");
    }
    const progressPages =
      userBookRead?.edition?.readingFormatId === audioReadingFormat.id
        ? 0
        : newProgress;
    const progressSeconds =
      userBookRead?.edition?.readingFormatId === audioReadingFormat.id
        ? newProgress
        : 0;

    return updateUserBookRead({
      client,
      id: userBookRead.id,
      object: {
        ...userBookRead,
        ...{ progressPages, progressSeconds },
      },
    })
      .then(updateUserBookReadCache)
      .catch(catchErrorMessage);
  };

  const deleteDate = () => {
    return deleteUserBookRead({ client, id: userBookRead.id }).then(
      newUserBookReadId => {
        if (bookId) {
          dispatch(removeUserBookRead({ bookId, userBookReadId }));
        }
        return newUserBookReadId;
      },
    );
  };

  return {
    deleteDate,
    setUserBookReadId,
    userBookRead,
    updateEdition,
    updateDates,
    updateProgress,
  };
}
